<script setup lang="ts">
const { isHelpSlideoverOpen } = useDashboard()
const { isDashboardSearchModalOpen } = useUIState()
const { metaSymbol } = useShortcuts()

const toast = useToast()

const userInfoDialogOpen = ref(false)

const openUserInfo = () => {
  userInfoDialogOpen.value = true
}

const authUser = useState('authUser')
const router = useRouter()
const items = computed(() => [
  [{
    slot: 'account',
    label: '',
    disabled: true
  }], [{
		label: '用户信息',
		icon: 'i-heroicons-user',
		click: () => {
			openUserInfo()
		}
	}], [{
    label: '退出登录',
    icon: 'i-heroicons-arrow-left-on-rectangle',
    click: () => {
      authLogout()
    }
  }]
])

const rePasswordDialogOpen = ref(false)
const rePasswordLoading = ref(false)

const rePasswordForm = ref({
  password: '',
  newPassword: '',
  reNewPassword: ''
})

const rePasswordDisabled = computed(() => {
  return rePasswordForm.value.password.trim() === '' || rePasswordForm.value.newPassword.trim() === '' || rePasswordForm.value.reNewPassword.trim() === ''
})

const openRePassword = () => {
  rePasswordDialogOpen.value = true
}

const confirmRePassword = () => {
  if(rePasswordForm.value.reNewPassword !== rePasswordForm.value.newPassword) {
    toast.add({
      title: '两次新密码输入不一致,请重新输入!',
      color: 'yellow'
    })
    return
  }

  rePasswordLoading.value = true

  postApi('/user/changePassword', {
    password: rePasswordForm.value.password,
    newPassword: rePasswordForm.value.newPassword,
    reNewPassword: rePasswordForm.value.reNewPassword
  }).then((res) => {
    if(res.code === 0) {
      toast.add({
        title: '密码修改成功,请重新登录'
      })

      rePasswordForm.value.password = ''
      rePasswordForm.value.newPassword = ''
      rePasswordForm.value.reNewPassword = ''

      setTimeout(() => {
        authLogout()
      }, 1000)
    } else {
      toast.add({
        title: res.msg || '修改密码失败,请稍后重试',
        color: 'red'
      })
    }
  }).catch(() => {
    toast.add({
      title: '发起请求失败,请检查网络或稍后重试',
      color: 'red'
    })
  }).finally(() => {
    rePasswordLoading.value = false
  })

}
</script>

<template>
  <UDashboardModal
      v-model="rePasswordDialogOpen"
      title="修改密码"
      preventClose
      :ui="{width: '!max-w-80'}"
  >

    <div class="flex flex-col gap-2">
      <UFormGroup label="当前密码">
        <UInput v-model="rePasswordForm.password" type="password" placeholder="请输入当前密码" />
      </UFormGroup>
      <UFormGroup label="设置新密码">
        <UInput v-model="rePasswordForm.newPassword" type="password" placeholder="请输入新密码" />
      </UFormGroup>
      <UFormGroup label="重复新密码">
        <UInput v-model="rePasswordForm.reNewPassword" type="password" placeholder="请再次输入新密码" />
      </UFormGroup>
    </div>

    <template #footer>
      <div class="flex w-full justify-between items-center">
        <div>
          <UButton color="gray" @click="rePasswordDialogOpen = false">取消</UButton>
        </div>
        <div>
          <UButton @click="confirmRePassword" :disabled="rePasswordDisabled">确认修改</UButton>
        </div>
      </div>
    </template>
  </UDashboardModal>

  <UDashboardModal
    v-model="userInfoDialogOpen"
    title="用户信息"
    preventClose
  >
    <div class="flex flex-col gap-2">
      <div class="flex gap-2">
        <div class="w-24 text-right text-gray-500">
          UID:
        </div>
        <div>
          {{ authUser?.id }}
        </div>
      </div>
      <div class="flex gap-2">
        <div class="w-24 text-right text-gray-500">
          用户名:
        </div>
        <div>
          {{ authUser?.username }}
        </div>
      </div>
	    <div class="flex gap-2">
		    <div class="w-24 text-right text-gray-500">
			    医生姓名:
		    </div>
		    <div>
			    {{ authUser?.profile?.name }}
		    </div>
	    </div>
      <div class="flex gap-2">
        <div class="w-24 text-right text-gray-500">
          手机号码:
        </div>
        <div>
          {{ authUser?.phone || '暂未填写' }}
        </div>
      </div>
      <div class="flex gap-2">
        <div class="w-24 text-right text-gray-500">
          电子邮箱:
        </div>
        <div>
          {{ authUser?.email || '暂未填写' }}
        </div>
      </div>
      <div class="flex gap-2">
        <div class="w-24 text-right text-gray-500">
          单位名称:
        </div>
        <div>
          {{ authUser?.hospital?.hospitalName || '暂无' }}
        </div>
      </div>
      <div class="flex gap-2">
        <div class="w-24 text-right text-gray-500">
          注册时间:
        </div>
        <div>
          {{ formatDate(authUser?.createAt, '人性化') }}
        </div>
      </div>
    </div>
    <template #footer>
      <div class="flex w-full justify-between items-center">
        <div>
          <UButton color="gray" @click="userInfoDialogOpen = false">关闭</UButton>
        </div>
        <div>
          <UButton @click="openRePassword">修改密码</UButton>
        </div>
      </div>
    </template>
  </UDashboardModal>

  <UDropdown mode="hover" :items="items" :ui="{ width: 'w-full', item: { disabled: 'cursor-text select-text' } }" :popper="{ strategy: 'absolute', placement: 'top' }" class="w-full">
    <template #default="{ open }">
      <UButton color="black" variant="ghost" class="w-full text-gray-100" :class="[open && 'bg-primary-400 dark:bg-gray-800']">
	      <template #default>
		      <div class="w-full flex items-center gap-1">
			      <div class="flex items-center">
			        <UIcon class="text-xl" name="i-heroicons-user-circle-20-solid" />
			      </div>
			      <div class="truncate">
				      {{ authUser?.profile?.name }}
			      </div>
		      </div>
	      </template>

        <template #trailing>
          <UIcon name="i-heroicons-ellipsis-vertical" class="w-5 h-5 ml-auto" />
        </template>
      </UButton>

    </template>

    <template #account>
      <div class="">
        <p >

        </p>
        <div></div>
        <p class="truncate font-medium text-gray-900 dark:text-white">
          {{ authUser?.username }}
        </p>
      </div>
    </template>
  </UDropdown>
</template>
